import { apiUrl } from "./config";
import { runWordExportJob } from "./wordExportJobs";
import { applyImageOverlaysToPdf } from "../components/document-processing/imageOverlays";

async function parseJson(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

async function fetchJobResponse(url, options) {
  const controller = new AbortController();
  const externalSignal = options?.signal;
  const abort = () => controller.abort();
  if (externalSignal?.aborted) controller.abort();
  else externalSignal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(() => controller.abort(), 90000);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    const result = await parseJson(response);
    return { response, result };
  } finally {
    clearTimeout(timer);
    externalSignal?.removeEventListener("abort", abort);
  }
}

function formatMatplotlibPreviewError(error) {
  const text = String(error || "").trim();
  if (!text) return "Matplotlib 預覽失敗";

  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const lastLine = lines[lines.length - 1] || text;
  const lineMatch = text.match(/File "<matplotlib-preview>", line (\d+)/);
  if (lineMatch) {
    return `Python Figure Code line ${lineMatch[1]} failed: ${lastLine}`;
  }
  return lastLine.length > 180 ? `${lastLine.slice(0, 180)}...` : lastLine;
}

function formatApiError(result, fallback) {
  if (!result || typeof result !== "object") return fallback;
  if (result.code === "openai_insufficient_quota") {
    return "OpenAI API 額度不足，請到 OpenAI Platform 購買 credits 或開啟 auto-reload 後再試。";
  }
  if (result.code === "openai_rate_limited") {
    return "OpenAI API 目前請求太多，請稍候再試。";
  }
  if (result.code === "openai_auth_failed") {
    return "OpenAI API key 無法使用，請檢查 billing、專案權限或更換 API key。";
  }
  if (result.error) return result.error;
  if (result.detail) return result.detail;

  const entries = Object.entries(result)
    .filter(([key]) => key !== "success" && key !== "code")
    .map(([key, value]) => {
      if (Array.isArray(value)) return `${key}: ${value.join("、")}`;
      if (value && typeof value === "object") return `${key}: ${formatApiError(value, "")}`;
      return `${key}: ${String(value)}`;
    })
    .filter(Boolean);
  return entries[0] || fallback;
}

function buildErrorResult(response, result, fallback) {
  return {
    success: false,
    error: formatApiError(result, fallback),
    code: result?.code,
    diagnostics: result?.diagnostics,
    status: response.status,
  };
}

export const extractPdfText = async ({
  file,
  mode = "fast",
  quality = "standard",
  authToken,
}) => {
  if (!authToken) {
    console.error("Access token not found. The user might not be logged in.");
    return { success: false, error: "User not authenticated" };
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("mode", mode);
  formData.append("quality", quality);

  try {
    const response = await fetch(`${apiUrl}/pdf/extract/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: formData,
    });

    const result = await parseJson(response);
    if (response.ok && result.success) {
      return {
        success: true,
        text: result.text || "",
        model: result.model || "",
        verifyModel: result.verify_model || "",
        verifyEnabled: Boolean(result.verify_enabled),
      };
    }

    return buildErrorResult(response, result, "Failed to extract text");
  } catch (error) {
    console.error("Error extracting PDF text:", error);
    return {
      success: false,
      error: "Error extracting PDF text",
      status: 0,
    };
  }
};

export const startPdfExtractJob = async ({
  file,
  mode = "math_markdown",
  quality = "ultra",
  authToken,
  clientRequestId,
}) => {
  if (!authToken) {
    console.error("Access token not found. The user might not be logged in.");
    return { success: false, error: "User not authenticated" };
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("mode", mode);
  formData.append("quality", quality);
  if (clientRequestId) formData.append("client_request_id", clientRequestId);

  try {
    const { response, result } = await fetchJobResponse(`${apiUrl}/pdf/extract/jobs/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: formData,
    });

    if (response.ok && result.success && result.job) {
      return { success: true, job: result.job };
    }

    return buildErrorResult(response, result, "Failed to start extraction job");
  } catch (error) {
    console.error("Error starting PDF extraction job:", error);
    return {
      success: false,
      error: "Error starting PDF extraction job",
      status: 0,
    };
  }
};

export const getPdfExtractJob = async ({ jobId, authToken }) => {
  if (!authToken) {
    console.error("Access token not found. The user might not be logged in.");
    return { success: false, error: "User not authenticated" };
  }

  try {
    const { response, result } = await fetchJobResponse(`${apiUrl}/pdf/extract/jobs/${jobId}/`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (response.ok && result.success && result.job) {
      return { success: true, job: result.job };
    }

    return buildErrorResult(response, result, "Failed to fetch extraction job");
  } catch (error) {
    console.error("Error fetching PDF extraction job:", error);
    return {
      success: false,
      error: "Error fetching PDF extraction job",
      status: 0,
    };
  }
};

export const listPdfExtractJobs = async ({ authToken }) => {
  if (!authToken) return { success: false, error: "請先登入。" };
  try {
    const { response, result } = await fetchJobResponse(`${apiUrl}/pdf/extract/jobs/`, {
      method: "GET", headers: { Authorization: `Bearer ${authToken}` },
    });
    if (response.ok && result.success && Array.isArray(result.jobs)) return result;
    return buildErrorResult(response, result, "尚無法讀取 OCR 工作清單。");
  } catch {
    return { success: false, error: "尚無法讀取 OCR 工作清單，請重試。", status: 0 };
  }
};

export const cancelPdfExtractJob = async ({ jobId, authToken, clientRequestId, keepalive = false }) => {
  if (!authToken) return { success: false, error: "請先登入。" };
  try {
    const { response, result } = await fetchJobResponse(
      jobId ? `${apiUrl}/pdf/extract/jobs/${encodeURIComponent(jobId)}/cancel/`
        : `${apiUrl}/pdf/extract/jobs/cancel/`,
      {
        method: "POST", keepalive,
        headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(clientRequestId ? { client_request_id: clientRequestId } : {}),
      },
    );
    if (response.ok && result.success && (result.job || result.cancelled)) return result;
    return buildErrorResult(response, result, "無法確認停止，請重試。");
  } catch {
    return { success: false, error: "無法確認停止，請檢查連線後重試。", status: 0 };
  }
};

export const startImageRedrawJob = async ({ file, instructions = "", authToken }) => {
  if (!authToken) return { success: false, error: "請先登入。" };

  const formData = new FormData();
  formData.append("image", file);
  formData.append("instructions", instructions);

  try {
    const { response, result } = await fetchJobResponse(`${apiUrl}/pdf/image-redraw/jobs/`, {
      method: "POST",
      headers: { Authorization: `Bearer ${authToken}` },
      body: formData,
    });
    if (response.ok && result.success && result.job?.id) {
      return { success: true, job: result.job };
    }
    return buildErrorResult(response, result, "無法建立圖片重畫工作。");
  } catch (error) {
    return { success: false, error: "無法連線至圖片重畫服務。", status: 0 };
  }
};

export const getImageRedrawJob = async ({ jobId, authToken }) => {
  if (!authToken) return { success: false, error: "請先登入。" };

  try {
    const { response, result } = await fetchJobResponse(
      `${apiUrl}/pdf/image-redraw/jobs/${encodeURIComponent(jobId)}/`,
      { method: "GET", headers: { Authorization: `Bearer ${authToken}` } },
    );
    if (response.ok && result.success && result.job) {
      return { success: true, job: result.job };
    }
    return buildErrorResult(response, result, "無法取得圖片重畫結果。");
  } catch (error) {
    return { success: false, error: "無法連線至圖片重畫服務。", status: 0 };
  }
};

export const startHandoutImportJob = async ({
  file,
  quality = "ultra",
  authToken,
}) => {
  if (!authToken) {
    console.error("Access token not found. The user might not be logged in.");
    return { success: false, error: "User not authenticated" };
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("quality", quality);

  try {
    const { response, result } = await fetchJobResponse(`${apiUrl}/pdf/handout-import/jobs/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: formData,
    });

    if (response.ok && result.success && result.job) {
      return { success: true, job: result.job };
    }

    return buildErrorResult(response, result, "Failed to start handout import job");
  } catch (error) {
    console.error("Error starting handout import job:", error);
    return {
      success: false,
      error: "Error starting handout import job",
      status: 0,
    };
  }
};

export const getHandoutImportJob = async ({ jobId, authToken }) => {
  if (!authToken) {
    console.error("Access token not found. The user might not be logged in.");
    return { success: false, error: "User not authenticated" };
  }

  try {
    const { response, result } = await fetchJobResponse(`${apiUrl}/pdf/handout-import/jobs/${jobId}/`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (response.ok && result.success && result.job) {
      return { success: true, job: result.job };
    }

    return buildErrorResult(response, result, "Failed to fetch handout import job");
  } catch (error) {
    console.error("Error fetching handout import job:", error);
    return {
      success: false,
      error: "Error fetching handout import job",
      status: 0,
    };
  }
};

export const processAiText = async ({
  text,
  workflow = "solution",
  authToken,
  model = "",
  verifyModel = "",
  verify,
}) => {
  if (!authToken) {
    console.error("Access token not found. The user might not be logged in.");
    return { success: false, error: "User not authenticated" };
  }

  try {
    const response = await fetch(`${apiUrl}/ai/process/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        text,
        workflow,
        model,
        verify_model: verifyModel,
        verify,
      }),
    });

    const result = await parseJson(response);
    if (response.ok && result.success) {
      return { success: true, text: result.text || "" };
    }

    return buildErrorResult(response, result, "Failed to process text");
  } catch (error) {
    console.error("Error processing text with AI:", error);
    return { success: false, error: "Error processing text with AI" };
  }
};

export const startAiProcessJob = async ({
  text,
  workflow = "solution",
  authToken,
  model = "",
  verifyModel = "",
  verify,
}) => {
  if (!authToken) {
    console.error("Access token not found. The user might not be logged in.");
    return { success: false, error: "User not authenticated" };
  }

  try {
    const response = await fetch(`${apiUrl}/ai/process/jobs/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        text,
        workflow,
        model,
        verify_model: verifyModel,
        verify,
      }),
    });

    const result = await parseJson(response);
    if (response.ok && result.success && result.job) {
      return { success: true, job: result.job };
    }

    return buildErrorResult(response, result, "Failed to start AI job");
  } catch (error) {
    console.error("Error starting AI processing job:", error);
    return { success: false, error: "Error starting AI processing job", status: 0 };
  }
};

export const getAiProcessJob = async ({ jobId, authToken }) => {
  if (!authToken) {
    console.error("Access token not found. The user might not be logged in.");
    return { success: false, error: "User not authenticated" };
  }

  try {
    const response = await fetch(`${apiUrl}/ai/process/jobs/${jobId}/`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    const result = await parseJson(response);
    if (response.ok && result.success && result.job) {
      return { success: true, job: result.job };
    }

    return buildErrorResult(response, result, "Failed to fetch AI job");
  } catch (error) {
    console.error("Error fetching AI processing job:", error);
    return { success: false, error: "Error fetching AI processing job", status: 0 };
  }
};

export const buildAiSolutionFromImages = async ({
  files,
  text = "",
  authToken,
  model = "",
  verifyModel = "",
  verify,
}) => {
  if (!authToken) {
    console.error("Access token not found. The user might not be logged in.");
    return { success: false, error: "User not authenticated" };
  }

  const formData = new FormData();
  (files || []).forEach((file) => formData.append("files", file));
  formData.append("text", text || "");
  formData.append("model", model || "");
  formData.append("verify_model", verifyModel || "");
  if (verify !== undefined) formData.append("verify", verify ? "true" : "false");

  try {
    const response = await fetch(`${apiUrl}/ai/solution-from-images/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: formData,
    });

    const result = await parseJson(response);
    if (response.ok && result.success) {
      return {
        success: true,
        solutionText: result.solution_text || "",
        model: result.model || "",
        verifyModel: result.verify_model || "",
        verifyEnabled: Boolean(result.verify_enabled),
      };
    }

    return buildErrorResult(response, result, "圖片詳解產生失敗");
  } catch (error) {
    console.error("Error building AI solution from images:", error);
    return { success: false, error: "無法連線至圖片詳解服務", status: 0 };
  }
};

export const startAiSolutionFromImagesJob = async ({
  files,
  promptAssets = [],
  text = "",
  authToken,
  model = "",
  verifyModel = "",
  verify,
}) => {
  if (!authToken) {
    console.error("Access token not found. The user might not be logged in.");
    return { success: false, error: "User not authenticated" };
  }

  const formData = new FormData();
  (files || []).forEach((file) => formData.append("files", file));
  formData.append("text", text || "");
  formData.append("prompt_assets", JSON.stringify(promptAssets));
  formData.append("model", model || "");
  formData.append("verify_model", verifyModel || "");
  if (verify !== undefined) formData.append("verify", verify ? "true" : "false");

  try {
    const response = await fetch(`${apiUrl}/ai/solution-from-images/jobs/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: formData,
    });

    const result = await parseJson(response);
    if (response.ok && result.success && result.job) {
      return { success: true, job: result.job };
    }

    return buildErrorResult(response, result, "圖片詳解工作建立失敗");
  } catch (error) {
    console.error("Error starting AI solution image job:", error);
    return { success: false, error: "無法建立圖片詳解工作", status: 0 };
  }
};

export const getAiSolutionFromImagesJob = async ({ jobId, authToken }) => {
  if (!authToken) {
    console.error("Access token not found. The user might not be logged in.");
    return { success: false, error: "User not authenticated" };
  }

  try {
    const response = await fetch(`${apiUrl}/ai/solution-from-images/jobs/${jobId}/`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    const result = await parseJson(response);
    if (response.ok && result.success && result.job) {
      return { success: true, job: result.job };
    }

    return buildErrorResult(response, result, "圖片詳解工作查詢失敗");
  } catch (error) {
    console.error("Error fetching AI solution image job:", error);
    return { success: false, error: "無法查詢圖片詳解工作", status: 0 };
  }
};

export const sendAiChatMessage = async ({
  files,
  text = "",
  messages = [],
  authToken,
  model = "",
}) => {
  if (!authToken) {
    console.error("Access token not found. The user might not be logged in.");
    return { success: false, error: "User not authenticated" };
  }

  const formData = new FormData();
  (files || []).forEach((file) => formData.append("files", file));
  formData.append("text", text || "");
  formData.append("messages", JSON.stringify(messages || []));
  formData.append("model", model || "");

  try {
    const response = await fetch(`${apiUrl}/ai/chat/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: formData,
    });

    const result = await parseJson(response);
    if (response.ok && result.success) {
      return {
        success: true,
        replyText: result.reply_text || "",
        model: result.model || "",
        inputImageCount: Number(result.input_image_count || 0),
      };
    }

    return buildErrorResult(response, result, "AI 對話送出失敗");
  } catch (error) {
    console.error("Error sending AI chat message:", error);
    return { success: false, error: "無法連線至 AI 對話服務", status: 0 };
  }
};

// The backend renders previews one at a time per dyno. Pages with many
// question thumbnails would otherwise park every gunicorn thread on the render
// queue and stall unrelated API calls, so keep only a couple in flight.
const MATPLOTLIB_PREVIEW_MAX_IN_FLIGHT = 2;
const MATPLOTLIB_PREVIEW_BUSY_RETRIES = 3;
let matplotlibPreviewInFlight = 0;
const matplotlibPreviewWaiters = [];

// A cancelled preview is reported as data, not thrown, so callers that only
// ignore late results keep working.
export const MATPLOTLIB_PREVIEW_ABORTED = "matplotlib_preview_aborted";
const abortedPreviewResult = () => ({ success: false, aborted: true, code: MATPLOTLIB_PREVIEW_ABORTED, error: "預覽已取消", status: 0 });
const abortError = () => {
  const error = new Error("Matplotlib preview aborted");
  error.name = "AbortError";
  return error;
};

// Resolves once a slot is held; rejects (and leaves the queue) when the caller
// gives up first, so an abandoned preview never reaches the backend.
const acquireMatplotlibPreviewSlot = (signal) => {
  if (signal?.aborted) return Promise.reject(abortError());
  if (matplotlibPreviewInFlight < MATPLOTLIB_PREVIEW_MAX_IN_FLIGHT) {
    matplotlibPreviewInFlight += 1;
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const waiter = () => {
      signal?.removeEventListener?.("abort", onAbort);
      resolve();
    };
    const onAbort = () => {
      const index = matplotlibPreviewWaiters.indexOf(waiter);
      if (index >= 0) matplotlibPreviewWaiters.splice(index, 1);
      reject(abortError());
    };
    signal?.addEventListener?.("abort", onAbort, { once: true });
    matplotlibPreviewWaiters.push(waiter);
  });
};

const newPreviewRequestId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().replace(/-/g, "");
  }
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
};

// Requests that reached the backend; closing the tab withdraws them too.
const sentMatplotlibPreviews = new Map();

// Tell the backend to stop a render nobody is waiting for. keepalive lets the
// request outlive a closing page; failures only mean the render finishes.
const cancelSentMatplotlibPreview = (requestId, authToken) => {
  sentMatplotlibPreviews.delete(requestId);
  try {
    fetch(`${apiUrl}/pdf/matplotlib-preview/cancel/`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ request_id: requestId }),
    }).catch(() => {});
  } catch (error) {
    // Best effort only.
  }
};

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    sentMatplotlibPreviews.forEach((authToken, requestId) => cancelSentMatplotlibPreview(requestId, authToken));
  });
}

const releaseMatplotlibPreviewSlot = () => {
  const next = matplotlibPreviewWaiters.shift();
  if (next) next();
  else matplotlibPreviewInFlight -= 1;
};

const wait = (ms, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) { reject(abortError()); return; }
  const timer = setTimeout(() => {
    signal?.removeEventListener?.("abort", onAbort);
    resolve();
  }, ms);
  const onAbort = () => { clearTimeout(timer); reject(abortError()); };
  signal?.addEventListener?.("abort", onAbort, { once: true });
});

export const previewMatplotlibCode = async ({ code, authToken, signal }) => {
  if (!authToken) {
    console.error("Access token not found. The user might not be logged in.");
    return { success: false, error: "User not authenticated" };
  }

  try {
    await acquireMatplotlibPreviewSlot(signal);
  } catch (error) {
    return abortedPreviewResult();
  }
  const requestId = newPreviewRequestId();
  const withdraw = () => {
    if (sentMatplotlibPreviews.has(requestId)) cancelSentMatplotlibPreview(requestId, authToken);
  };
  signal?.addEventListener?.("abort", withdraw, { once: true });
  try {
    let response;
    let result;
    for (let attempt = 0; ; attempt += 1) {
      sentMatplotlibPreviews.set(requestId, authToken);
      response = await fetch(`${apiUrl}/pdf/matplotlib-preview/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ code, request_id: requestId }),
        signal,
      });
      sentMatplotlibPreviews.delete(requestId);
      result = await parseJson(response);
      const busy = response.status === 503 && result?.code === "matplotlib_preview_busy";
      if (!busy || attempt >= MATPLOTLIB_PREVIEW_BUSY_RETRIES) break;
      const retryAfter = Number(response.headers?.get?.("Retry-After")) || 3;
      await wait(retryAfter * 1000, signal);
    }

    if (response.ok && result.success) {
      return { success: true, image: result.image || "" };
    }

    return {
      success: false,
      error: formatMatplotlibPreviewError(
        result.error || result.detail || "Matplotlib 預覽失敗",
      ),
      status: response.status,
    };
  } catch (error) {
    if (signal?.aborted || error?.name === "AbortError") return abortedPreviewResult();
    console.error("Error previewing matplotlib code:", error);
    return { success: false, error: "無法連線至 Matplotlib 預覽服務", status: 0 };
  } finally {
    signal?.removeEventListener?.("abort", withdraw);
    sentMatplotlibPreviews.delete(requestId);
    releaseMatplotlibPreviewSlot();
  }
};

export const buildMathBankJson = async ({
  text,
  gradeId,
  unitId,
  defaultType = "calculation",
  defaultDifficulty = "U",
  authToken,
}) => {
  if (!authToken) {
    return { success: false, error: "User not authenticated" };
  }

  try {
    const response = await fetch(`${apiUrl}/pdf/math-bank-json/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        text,
        grade_id: gradeId,
        unit_id: unitId,
        default_type: defaultType,
        default_difficulty: defaultDifficulty,
      }),
    });

    const result = await parseJson(response);
    if (response.ok && result.success && result.payload) {
      return { success: true, payload: result.payload };
    }

    return {
      success: false,
      error: result.error || result.detail || "題目 JSON 轉換失敗",
      code: result.code,
      status: response.status,
    };
  } catch (error) {
    console.error("Error building math bank JSON:", error);
    return { success: false, error: "無法連線至題目 JSON 轉換服務", status: 0 };
  }
};

async function requestHandwritingPage({ file, colorMode, outputFormat, strength, manualRegions, authToken, pageNumber, signal }, preview) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener("abort", abort, { once: true });
  // Allow the backend's 28-second processing budget plus transfer time.
  const timeout = setTimeout(abort, 35000);
  const formData = new FormData();
  formData.append("file", file);
  formData.append("color_mode", colorMode);
  formData.append("strength", String(strength));
  formData.append("page_number", String(pageNumber));
  if (outputFormat) formData.append("output_format", outputFormat);
  if (manualRegions.length) formData.append("manual_regions", JSON.stringify(manualRegions));
  try {
    const response = await fetch(`${apiUrl}/pdf/${preview ? "preview" : "remove"}-handwriting/`, {
      method: "POST", headers: { Authorization: `Bearer ${authToken}` },
      body: formData, signal: controller.signal,
    });
    if (preview) {
      const result = await parseJson(response);
      if (!response.ok || !result.success) throw new Error(formatApiError(result, "筆跡預覽失敗"));
      const pages = result.pages || [];
      if (!pages.length) throw new Error("沒有收到頁面預覽。");
      return { pages, total: Number(result.page_count || pages.length) };
    }
    const type = response.headers.get("content-type") || "";
    if (!response.ok || !(type.includes("application/pdf") || type.startsWith("image/"))) {
      throw new Error(formatApiError(await parseJson(response), "筆跡去除服務沒有回傳可下載的檔案。"));
    }
    return {
      blob: await response.blob(),
      filename: getDownloadFilename(response, type.includes("application/pdf") ? "cleaned-document.pdf" : "cleaned-document.png"),
      total: Number(response.headers.get("x-source-pages") || 1),
      maskRatio: Number(response.headers.get("x-handwriting-mask-ratio") || 0),
    };
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error(signal?.aborted ? "已取消筆跡處理。" : `第 ${pageNumber} 頁處理逾時，請稍後再試或縮小檔案。`);
    }
    throw error;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
  }
}

function validateHandwritingInput({ file, authToken }) {
  if (!authToken) throw new Error("User not authenticated");
  if (!file) throw new Error("請先選擇 PDF 或圖片。");
  if (file.size > 30 * 1024 * 1024) throw new Error("筆跡去除檔案上限為 30 MB。");
}

function validateHandwritingPageCount(total, limit = 80) {
  if (!Number.isInteger(total) || total < 1 || total > limit) {
    throw new Error(`筆跡去除支援最多 ${limit} 頁。`);
  }
}

// Each request processes one page. PDF assembly stays in the browser so Heroku
// needs neither sticky sessions nor persistent local files/background threads.
export const removeHandwriting = async ({
  file, colorMode = "exam_auto", outputFormat = "pdf", strength = 2,
  manualRegions = [], imageOverlays = [], authToken, signal, onProgress,
}) => {
  try {
    validateHandwritingInput({ file, authToken });
    const options = { file, colorMode, outputFormat, strength, manualRegions, authToken, signal };
    const first = await requestHandwritingPage({ ...options, pageNumber: 1 }, false);
    validateHandwritingPageCount(first.total);
    onProgress?.(1, first.total);
    if (first.total === 1) return { success: true, ...first, pageCount: 1,
      blob: await applyImageOverlaysToPdf(first.blob, imageOverlays, signal) };
    const { PDFDocument } = await import("pdf-lib");
    const merged = await PDFDocument.create();
    let ratio = 0;
    for (let page = 1; page <= first.total; page += 1) {
      if (signal?.aborted) throw new Error("已取消筆跡處理。");
      const result = page === 1 ? first : await requestHandwritingPage({ ...options, pageNumber: page }, false);
      if (result.total !== first.total) throw new Error("PDF 頁數不一致，請重新選擇檔案。");
      const document = await PDFDocument.load(await result.blob.arrayBuffer());
      if (document.getPageCount() !== 1) throw new Error("逐頁匯出回傳了非預期的頁數。");
      const [copied] = await merged.copyPages(document, [0]);
      merged.addPage(copied);
      ratio += result.maskRatio;
      onProgress?.(page, first.total);
    }
    return { success: true, blob: await applyImageOverlaysToPdf(new Blob([await merged.save()], { type: "application/pdf" }), imageOverlays, signal),
      filename: first.filename, pageCount: first.total, maskRatio: ratio / first.total };
  } catch (error) {
    return { success: false, error: error.message || "無法連線至筆跡去除服務", status: 0 };
  }
};

export async function repairHandwritingImage({ image, instructions = "", authToken, signal, onProgress }) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener("abort", abort, { once: true });
  const timeout = setTimeout(abort, 330000);
  const request = async (url, options = {}) => {
    const response = await fetch(`${apiUrl}/pdf/image-repair/jobs/${url}`, {
      ...options, headers: { Authorization: `Bearer ${authToken}` }, signal: controller.signal,
    });
    const result = await parseJson(response);
    if (!response.ok || !result.success) throw new Error(formatApiError(result, "圖片修復失敗。"));
    return result.job;
  };
  const pause = () => new Promise((resolve, reject) => {
    const done = () => { controller.signal.removeEventListener("abort", cancel); resolve(); };
    const timer = setTimeout(done, 1500);
    const cancel = () => { clearTimeout(timer); reject(new DOMException("Cancelled", "AbortError")); };
    if (controller.signal.aborted) cancel();
    else controller.signal.addEventListener("abort", cancel, { once: true });
  });
  try {
    if (!authToken) throw new Error("請先登入。");
    const form = new FormData();
    form.append("image", image, "crop.png"); form.append("instructions", instructions);
    let job = await request("", { method: "POST", body: form });
    const id = job?.id;
    if (!id) throw new Error("未收到圖片修復工作編號。");
    while (job.status === "queued" || job.status === "running") {
      onProgress?.(job.status === "queued" ? "圖片修復排隊中…" : "正在修復框選圖片…");
      await pause();
      job = await request(`${encodeURIComponent(id)}/`);
    }
    if (job.status !== "success" || !job.image?.startsWith("data:image/png;base64,")) {
      throw new Error(job.error || "未收到可用的修復圖片。");
    }
    return { success: true, image: job.image };
  } catch (error) {
    return { success: false, error: error.name === "AbortError"
      ? (signal?.aborted ? "已停止等待修復。" : "圖片修復逾時，請稍後重試。") : error.message };
  } finally {
    clearTimeout(timeout); signal?.removeEventListener("abort", abort);
  }
}

export const previewHandwriting = async ({
  file, colorMode = "exam_auto", strength = 2, manualRegions = [],
  maxPages = 80, authToken, signal, onProgress,
}) => {
  try {
    validateHandwritingInput({ file, authToken });
    const options = { file, colorMode, strength, manualRegions, authToken, signal };
    const pages = [];
    let total = 1;
    for (let page = 1; page <= total; page += 1) {
      if (signal?.aborted) throw new Error("已取消筆跡處理。");
      const result = await requestHandwritingPage({ ...options, pageNumber: page }, true);
      validateHandwritingPageCount(result.total, maxPages);
      if (page > 1 && result.total !== total) throw new Error("PDF 頁數不一致，請重新選擇檔案。");
      if (result.pages.length !== 1 || result.pages[0].page !== page) throw new Error("逐頁預覽回傳了非預期的頁碼。");
      total = result.total;
      pages.push(...result.pages);
      onProgress?.(page, total, [...pages]);
    }
    return { success: true, pages };
  } catch (error) {
    return { success: false, error: error.message || "無法連線至筆跡預覽服務", status: 0 };
  }
};

export const parseWordDocument = async ({
  file,
  includeAssets = true,
  mode = "fast",
  authToken,
}) => {
  if (!authToken) {
    return { success: false, error: "User not authenticated" };
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("include_assets", includeAssets ? "true" : "false");
  formData.append("mode", mode);

  try {
    const response = await fetch(`${apiUrl}/word/parse/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: formData,
    });
    const result = await parseJson(response);

    if (response.ok && result.success && result.document) {
      return { success: true, document: result.document };
    }

    if (response.status === 503) {
      return {
        success: false,
        error: "Word 解析服務暫時忙碌或檔案處理太久，請先改用快速解析，或把 Word 拆小後再試。",
        code: result.code,
        status: response.status,
      };
    }

    return {
      success: false,
      error: result.error || result.detail || "Word 文件解析失敗",
      code: result.code,
      status: response.status,
    };
  } catch (error) {
    console.error("Error parsing Word document:", error);
    return {
      success: false,
      error: "無法連線至 Word 解析服務；如果檔案很大，可能是伺服器解析太久被中斷，請先用快速解析。",
      status: 0,
    };
  }
};

export const fetchWordTemplates = async ({ authToken }) => {
  if (!authToken) {
    return { success: false, error: "User not authenticated" };
  }

  try {
    const response = await fetch(`${apiUrl}/word/templates/`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });
    const result = await parseJson(response);

    if (response.ok && result.success) {
      return {
        success: true,
        templates: result.templates || [],
        defaultTemplateId:
          result.default_template_id || "exam_paper",
      };
    }

    return {
      success: false,
      error: result.error || result.detail || "無法取得 Word 模板",
      status: response.status,
    };
  } catch (error) {
    console.error("Error fetching Word templates:", error);
    return { success: false, error: "無法連線至 Word 模板服務", status: 0 };
  }
};

const bilingualPreviewPath = (templateId) => templateId === "bilingual_chinese_first_exam_paper"
  ? "bilingual-chinese-first-preview" : "bilingual-preview";

export const previewBilingualExam = async ({ document, questionIds, title, sectionMode, bilingualTranslations, templateId, authToken }) => {
  if (!authToken) return { success: false, error: "User not authenticated" };
  try {
    const response = await fetch(`${apiUrl}/word/${bilingualPreviewPath(templateId)}/`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ ...(document ? { document } : {
        question_ids: questionIds, title, section_mode: sectionMode,
        ...(bilingualTranslations ? { bilingual_translations: bilingualTranslations } : {}),
      }), ...(templateId ? { template_id: templateId } : {}) }),
    });
    const result = await parseJson(response);
    return response.ok ? { ...result, success: result.success !== false }
      : buildErrorResult(response, result, "雙語考卷翻譯失敗");
  } catch (error) {
    return { success: false, error: "無法連線至雙語考卷翻譯服務", status: 0 };
  }
};

export const startBilingualPreviewJob = async ({ document, questionIds, title, sectionMode, bilingualTranslations, templateId, clientRequestId, authToken, signal }) => {
  if (!authToken) return { success: false, error: "User not authenticated" };
  try {
    const { response, result } = await fetchJobResponse(`${apiUrl}/word/${bilingualPreviewPath(templateId)}/jobs/`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      signal,
      body: JSON.stringify({ ...(document ? { document } : {
        question_ids: questionIds, title, section_mode: sectionMode,
        ...(bilingualTranslations ? { bilingual_translations: bilingualTranslations } : {}),
      }), ...(templateId ? { template_id: templateId } : {}), client_request_id: clientRequestId }),
    });
    return response.ok && result.success && result.job?.id
      ? { success: true, job: result.job }
      : buildErrorResult(response, result, "無法建立雙語翻譯工作");
  } catch (error) {
    return { success: false, cancelled: signal?.aborted, error: "無法連線至雙語考卷翻譯服務", status: 0 };
  }
};

export const getBilingualPreviewJob = async ({ jobId, requestToken, templateId, authToken, signal }) => {
  if (!authToken) return { success: false, error: "User not authenticated" };
  try {
    const { response, result } = await fetchJobResponse(`${apiUrl}/word/${bilingualPreviewPath(templateId)}/jobs/${encodeURIComponent(jobId)}/`, {
      method: "GET", signal,
      headers: { Authorization: `Bearer ${authToken}`, "X-Bilingual-Job-Token": requestToken },
    });
    return response.ok && result.success && result.job?.id
      ? { success: true, job: result.job }
      : buildErrorResult(response, result, "無法取得雙語翻譯進度");
  } catch (error) {
    return { success: false, cancelled: signal?.aborted, error: "翻譯進度連線暫時中斷", status: 0 };
  }
};

const newBilingualRequestId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const previewBilingualExamJob = async ({ signal, onProgress, shouldContinue = () => true, pollInterval = 2000, clientRequestId, ...options }) => {
  if (!options.authToken) return { success: false, error: "User not authenticated" };
  const isActive = () => !signal?.aborted && shouldContinue();
  const cancelled = () => ({ success: false, cancelled: true });
  const pause = () => new Promise((resolve) => {
    const finish = () => { clearTimeout(timer); signal?.removeEventListener("abort", finish); resolve(isActive()); };
    const timer = setTimeout(finish, pollInterval);
    signal?.addEventListener("abort", finish, { once: true });
    if (!isActive()) finish();
  });
  const canRetry = (result) => result.status === 0 || result.status === 429 || result.status >= 500;
  const retryMessage = (result) => result.status
    ? `進度服務回報 HTTP ${result.status}，正在重新查詢同一個翻譯工作。${result.error || ""}`
    : "進度查詢連線失敗或逾時，正在重新連線；不會重新生成英文。";
  try {
    if (!isActive()) return cancelled();
    const requestToken = clientRequestId || newBilingualRequestId();
    let started;
    do {
      started = await startBilingualPreviewJob({ ...options, clientRequestId: requestToken, signal });
      if (!isActive()) return cancelled();
      if (!started.success) {
        if (!canRetry(started)) return started;
        onProgress?.({ status: "queued", retrying: true, retryMessage: retryMessage(started) });
        if (!await pause()) return cancelled();
      }
    } while (!started.success);
    let job = started.job;
    const jobId = job.id;
    const pollToken = job.request_token || requestToken;
    let retrying = false;
    let connectionMessage = "";
    while (isActive()) {
      onProgress?.({ ...job, retrying, retryMessage: connectionMessage });
      if (job.status === "success") {
        return job.result?.document ? { ...job.result, success: true, jobId, job } : {
          success: false, jobId, error: "翻譯工作完成，但沒有收到預覽內容。",
        };
      }
      if (job.status === "error") return {
        success: false, jobId, error: formatApiError(job, "雙語考卷翻譯失敗"), code: job.code, status: job.status_code,
      };
      if (!["queued", "running"].includes(job.status)) return { success: false, jobId, error: "無法辨識翻譯工作狀態。" };
      if (!await pause()) return cancelled();
      const next = await getBilingualPreviewJob({ jobId, requestToken: pollToken, templateId: options.templateId, authToken: options.authToken, signal });
      if (!isActive()) return cancelled();
      if (next.success) { job = next.job; retrying = false; }
      else {
        if (!canRetry(next)) return { ...next, jobId };
        retrying = true;
        connectionMessage = retryMessage(next);
      }
    }
    return cancelled();
  } catch (error) {
    return isActive() ? { success: false, error: error.message || "無法建立雙語翻譯工作。" } : cancelled();
  }
};

export const generateWordDocument = async ({
  document,
  filename = "questions.docx",
  templateId = "exam_paper",
  authToken,
  mixedVersion,
  signal,
}) => {
  if (!authToken) {
    return { success: false, error: "User not authenticated" };
  }

  try {
    const response = await fetch(`${apiUrl}/word/generate/`, {
      method: "POST",
      ...(signal ? { signal } : {}),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        document,
        filename,
        template_id: templateId,
        ...(document?.metadata?.mode === "mixed" && mixedVersion ? { mixed_version: mixedVersion } : {}),
      }),
    });

    if (response.ok) {
      const blob = await response.blob();
      return {
        success: true,
        blob,
        filename: getDownloadFilename(response, filename),
      };
    }

    const result = await parseJson(response);
    return {
      success: false,
      error: result.error || result.detail || "Word 文件產生失敗",
      code: result.code,
      status: response.status,
    };
  } catch (error) {
    console.error("Error generating Word document:", error);
    return { success: false, error: "無法連線至 Word 產生服務", status: 0 };
  }
};

export const generateWordFromBank = async ({
  subject, questionIds, title = "題庫選題講義", filename = "題庫選題講義.docx",
  templateId = "junior_math_handout", examRange = "", mode = "teaching", mixedVersion, sectionMode = "unit",
  handoutOptions, recordWorkspace = "picker", recordPayload, bilingualTranslations,
  authToken, signal, onProgress,
}) => {
  try {
    const result = await runWordExportJob({ source: "bank", authToken, signal, onProgress,
      payload: {
        question_ids: questionIds, ...(subject ? { subject } : {}), title, filename,
        template_id: templateId, exam_range: examRange, mode, section_mode: sectionMode,
        ...(mode === "mixed" && mixedVersion ? { mixed_version: mixedVersion } : {}),
        ...(handoutOptions ? { handout_options: handoutOptions } : {}),
        record_workspace: recordWorkspace,
        ...(recordPayload ? { record_payload: recordPayload } : {}),
        ...(bilingualTranslations ? { bilingual_translations: bilingualTranslations } : {}),
      },
    });
    return { success: true, ...result };
  } catch (error) {
    return { success: false, error: error.message || "無法連線至題庫講義產生服務",
      status: error.status || 0, code: error.code, cancelled: Boolean(error.cancelled),
      ...(error.cancellationUnconfirmed ? { cancellationUnconfirmed: true } : {}) };
  }
};

export const generateWordAnswerSheet = async ({
  file,
  filename = "answer-sheet.docx",
  rows = [],
  schoolLevel = "",
  authToken,
}) => {
  if (!authToken) {
    return { success: false, error: "User not authenticated" };
  }

  const formData = new FormData();
  if (file) formData.append("file", file);
  formData.append("filename", filename);
  if (schoolLevel) formData.append("school_level", schoolLevel);
  if (rows.length) {
    formData.append("rows", JSON.stringify(rows));
  }

  try {
    const response = await fetch(`${apiUrl}/word/answer-sheet/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: formData,
    });

    if (response.ok) {
      const blob = await response.blob();
      return {
        success: true,
        blob,
        filename: getDownloadFilename(response, filename),
        rowCount: Number(response.headers.get("x-answer-row-count") || 0),
      };
    }

    const result = await parseJson(response);
    return {
      success: false,
      error: result.error || result.detail || "簡答 Word 產生失敗",
      code: result.code,
      status: response.status,
    };
  } catch (error) {
    console.error("Error generating Word answer sheet:", error);
    return { success: false, error: "無法連線至簡答 Word 產生服務", status: 0 };
  }
};

export const previewWordAnswerSheet = async ({ file, authToken }) => {
  if (!authToken) {
    return { success: false, error: "User not authenticated" };
  }

  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await fetch(`${apiUrl}/word/answer-sheet/preview/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: formData,
    });

    const result = await parseJson(response);
    if (response.ok && result.success) {
      return {
        success: true,
        rows: result.rows || [],
        rowCount: Number(result.row_count || result.rows?.length || 0),
      };
    }

    return {
      success: false,
      error: result.error || result.detail || "簡答預覽失敗",
      code: result.code,
      status: response.status,
    };
  } catch (error) {
    console.error("Error previewing Word answer sheet:", error);
    return { success: false, error: "無法連線至簡答預覽服務", status: 0 };
  }
};

function getDownloadFilename(response, fallback) {
  const disposition = response.headers.get("content-disposition") || "";
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] || fallback;
}
